/**
 * 内置模拟机：一辆在浏览器里跑、但说**真实 MAVLink**的虚拟机体。
 *
 * 它刻意不走捷径（不用 JS 对象直达上层）：出帧走同一套 `encodeFrame`，
 * 入帧走同一个 `MavlinkParser`。因此它替换真机时改的只是 `Transport` 的实现，
 * 上层一行都不用动；反过来，上层在模拟机上跑通的东西到真机上也不会突然失效。
 *
 * 第二个用途是**可重复**：真实链路里"每 137 帧丢一次"这种问题无法复现，这里可以精确注入。
 *
 * 范围说明：这里是"能自洽运行的最小 ArduPilot 子集"，
 * 飞行模式编号与可用命令的**权威表**属于 `@hgc/ardupilot`（后续阶段）。
 */
import {
  MavlinkParser,
  allMessages,
  decodePayload,
  encodeFrame,
  messageByName,
  type MavlinkFrame,
  type MessageValues,
} from '@hgc/mavlink';

import { SimMission, type MissionUploadStep, type SimMissionItem } from './mission';

/** `MAV_AUTOPILOT_ARDUPILOTMEGA` */
export const AUTOPILOT_ARDUPILOTMEGA = 3;

/**
 * Copter 飞行模式编号。
 *
 * ArduPilot 把 `packet.custom_mode` 原样交给 `set_mode()`，
 * 所以 `HEARTBEAT.custom_mode` **就是**模式编号本身，不是位域。
 */
export const COPTER_MODES = {
  STABILIZE: 0,
  ACRO: 1,
  ALT_HOLD: 2,
  AUTO: 3,
  GUIDED: 4,
  LOITER: 5,
  RTL: 6,
  CIRCLE: 7,
  LAND: 9,
  POSHOLD: 16,
  BRAKE: 17,
} as const;

export const MAV_TYPE_FIXED_WING = 1;
export const MAV_TYPE_QUADROTOR = 2;

export const MAV_STATE_STANDBY = 3;
export const MAV_STATE_ACTIVE = 4;

export const MAV_MODE_FLAG_CUSTOM_MODE_ENABLED = 1;
export const MAV_MODE_FLAG_AUTO_ENABLED = 4;
export const MAV_MODE_FLAG_GUIDED_ENABLED = 8;
export const MAV_MODE_FLAG_STABILIZE_ENABLED = 16;
export const MAV_MODE_FLAG_SAFETY_ARMED = 128;

/**
 * 振动读数的基线（单位 m/s/s，与 `VIBRATION` 消息同一量纲）。
 *
 * 量级取真机经验区间的下沿：装配良好时三轴在 5~15，超过 30 说明该查桨与减震，
 * 超过 60 基本是明显异常。未解锁时电机不转，只剩传感器底噪，所以基线低得多。
 *
 * 它**不是常量**：读数必须随状态与时间变化。恒定值会让"振动过大"告警和振动曲线
 * 永远测不出东西 —— 而这两件事正是这些读数存在的唯一理由。
 */
const VIBRATION_BASE_ARMED = 20;
const VIBRATION_BASE_DISARMED = 4;

/**
 * 触发一次"加速度计裁剪"的瞬时值（约 3g）。
 *
 * 真机上 clipping 计的是"加速度计打到量程"的次数（量程常为 ±16g），
 * 所以好航时里它长时间是 0。模拟机刻意把这个阈值放在**偶尔会被越过**的位置：
 * 否则计数永远是 0，界面上那个读数与"发生过硬冲击"的判据就没有数据可测。
 * 统计按 tick 计（而不是按 VIBRATION 的推送频率）—— 真机上它也是传感器驱动的，
 * 与遥测流的频率无关。
 */
const VIBRATION_CLIP_THRESHOLD = 32;

/**
 * 任务执行时每个航点的停留时长。
 *
 * 真实航点的间隔按飞行速度算，动辄几十秒；模拟机是给人看的，
 * 取 4 秒才能在几十秒内看完一次"任务跑起来、进度条往前走"。
 * 这是**加速过的**数字，不是航速模型。
 */
const MISSION_ITEM_DWELL_MS = 4000;

/** 模拟机响应的命令编号（权威表在 `@hgc/ardupilot`） */
export const SIM_COMMANDS = {
  NAV_LAND: 21,
  NAV_TAKEOFF: 22,
  NAV_RETURN_TO_LAUNCH: 20,
  DO_SET_MODE: 176,
  DO_SET_HOME: 179,
  COMPONENT_ARM_DISARM: 400,
  RUN_PREARM_CHECKS: 401,
  SET_MESSAGE_INTERVAL: 511,
  REQUEST_MESSAGE: 512,
  MISSION_START: 300,
  PREFLIGHT_CALIBRATION: 241,
  DO_START_MAG_CAL: 42424,
} as const;

/** `MAV_MISSION_RESULT` 里模拟机会用到的几个 */
export const MISSION_ACCEPTED = 0;
export const MISSION_ERROR = 1;
export const MISSION_UNSUPPORTED = 3;
export const MISSION_INVALID = 5;

/** `MAV_CMD_REQUEST_MESSAGE` 里能点名的消息 ID */
export const SIM_REQUESTABLE_MESSAGES = {
  AUTOPILOT_VERSION: 148,
  AVAILABLE_MODES: 435,
  HOME_POSITION: 242,
} as const;

export const MAV_RESULT_ACCEPTED = 0;
export const MAV_RESULT_UNSUPPORTED = 3;
export const MAV_RESULT_FAILED = 4;

/**
 * 模拟机自报的协议能力位（`MAV_PROTOCOL_CAPABILITY`）。
 *
 * 位值已与生成表核对；用移位而不是魔数，是为了让"少报了一位"这种错误一眼看得出来 ——
 * 上层会根据这些位决定走 INT 还是 float 版本的接口，报错了就会走上错误的路径。
 */
export const SIM_PROTOCOL_CAPABILITIES =
  (1 << 0) | // MISSION_FLOAT      —— 支持 MISSION_ITEM（float 版）
  (1 << 1) | // PARAM_FLOAT        —— 支持 PARAM_VALUE（float 版）
  (1 << 2) | // MISSION_INT        —— 支持 MISSION_ITEM_INT
  (1 << 3) | // COMMAND_INT        —— 支持 COMMAND_INT
  (1 << 5) | // FTP                —— 支持 MAVLink FTP
  (1 << 6) | // SET_ATTITUDE_TARGET
  (1 << 8) | // SET_POSITION_TARGET_GLOBAL_INT
  (1 << 11) | // FLIGHT_TERMINATION
  (1 << 12) | // COMPASS_CALIBRATION
  (1 << 13) | // MAVLINK2
  (1 << 14) | // MISSION_FENCE
  (1 << 15); // MISSION_RALLY

/** `MAV_MAG_CAL_STATUS` */
export const MAG_CAL_RUNNING_STEP_ONE = 2;
export const MAG_CAL_SUCCESS = 4;

/** 校准过程中播报几帧进度（够界面画出进度条，又不至于淹没测试输出） */
const MAG_CAL_PROGRESS_STEPS = 5;

export type SimulatedFault =
  | 'none'
  /** 完全不产生输出：对端表现为断链 */
  | 'offline'
  /** 按概率丢帧，模拟数传链路的随机丢包 */
  | 'flaky'
  /** 继续按频率发帧但内容冻结：模拟"卡死但仍占着链路"，这是最难排查的一类故障 */
  | 'frozen'
  /**
   * 遥测照常推送，但**地面站发出的任何帧都被丢弃**：
   * 表现为"链路良好、界面上一切正常，但命令石沉大海"。
   *
   * 与 `offline` 的区别正是它的价值所在：`offline` 一眼就能看出来，
   * 而这一种会让人去怀疑界面、怀疑命令、怀疑参数 —— 怀疑所有不是原因的地方。
   */
  | 'silent-commands';

export interface SimulatedVehicleOptions {
  readonly systemId?: number;
  readonly componentId?: number;
  readonly kind?: 'copter' | 'plane';
  readonly version?: 1 | 2;
  readonly now?: () => number;
  readonly homeLat?: number;
  readonly homeLng?: number;
  readonly homeAltitudeM?: number;
  /** 注入伪随机源后，`flaky` 故障可复现 */
  readonly random?: () => number;
}

export interface SimulatedVehicleSnapshot {
  readonly armed: boolean;
  readonly mode: number;
  readonly modeName: string;
  readonly batteryPercent: number;
  readonly latitude: number;
  readonly longitude: number;
  readonly altitudeM: number;
  readonly satellites: number;
}

/**
 * 默认遥测集合，频率照着 ArduPilot 出厂默认（`SRx_*`）来，而不是"越高越好"：
 * 界面必须在**真实**的默认频率下就好用，否则等于拿实验室条件掩盖问题。
 */
const DEFAULT_STREAMS: ReadonlyArray<readonly [string, number]> = [
  ['HEARTBEAT', 1],
  ['SYS_STATUS', 1],
  ['ATTITUDE', 10],
  ['GLOBAL_POSITION_INT', 2],
  ['GPS_RAW_INT', 1],
  ['VFR_HUD', 2],
  ['BATTERY_STATUS', 0.5],
  ['RC_CHANNELS', 1],
  ['EKF_STATUS_REPORT', 1],
  /*
   * 振动：`EKF_STATUS_REPORT` 只有协方差，**没有**振动量，所以界面上的振动读数
   * 只能来自 `VIBRATION`。模拟机不推它的话，那个面板就永远是空的 ——
   * 而"空面板"在开发期最容易被当成"还没接上"而忽略掉。
   *
   * 1Hz 的理由：振动是慢变量，看的是趋势与 clipping 累计值，不是瞬时尖峰；
   * 核心层的 `TELEMETRY_STALE_MS.vibration` 也是按秒级名义频率给的 5 秒。
   * 再往上加只会在低速链路上挤占别的流。
   */
  ['VIBRATION', 1],
  /*
   * 任务进度：`MISSION_CURRENT` 在上游是**一直推**的
   * （`common.xml` 的说明：This message should be streamed all the time (nominally at 1Hz)），
   * 任务进度条读的就是它的 `seq`。这里照 1Hz 推。
   */
  ['MISSION_CURRENT', 1],
];

/** 一小份 ArduPilot 风格参数表（真实元数据来自上游 pdef，属 `@hgc/ardupilot`） */
const PARAMETERS: ReadonlyArray<readonly [string, number]> = [
  ['FORMAT_VERSION', 120],
  ['SYSID_THISMAV', 1],
  ['FRAME_CLASS', 1],
  ['FRAME_TYPE', 1],
  ['ARMING_CHECK', 1],
  ['BATT_CAPACITY', 5200],
  ['BATT_LOW_VOLT', 14],
  ['BATT_CRT_VOLT', 13.2],
  ['FLTMODE1', 0],
  ['FLTMODE2', 2],
  ['FLTMODE3', 5],
  ['FLTMODE4', 6],
  ['FLTMODE5', 3],
  ['FLTMODE6', 4],
  ['FENCE_ENABLE', 0],
  ['FENCE_RADIUS', 300],
  ['FENCE_ALT_MAX', 120],
  ['RTL_ALT', 1500],
  ['WPNAV_SPEED', 500],
  ['ATC_RAT_RLL_P', 0.135],
  ['ATC_RAT_PIT_P', 0.135],
  ['ATC_RAT_YAW_P', 0.18],
  ['MOT_SPIN_ARM', 0.1],
  ['LOG_BITMASK', 176126],
];

/** `MAV_PARAM_TYPE_REAL32` —— ArduPilot 的参数在线上都是 float */
const PARAM_TYPE_REAL32 = 9;

/** 每个 tick 回送的参数条数。真实链路是把参数当流推的，不会是"一条一个 tick"。 */
const PARAMS_PER_TICK = 8;

interface ParamRecord {
  readonly name: string;
  value: number;
}

interface StreamSpec {
  name: string;
  rateHz: number;
  lastSentAt: number;
}

export class SimulatedVehicle {
  readonly systemId: number;
  readonly componentId: number;
  readonly kind: 'copter' | 'plane';
  readonly version: 1 | 2;

  private sink: ((bytes: Uint8Array) => void) | null = null;
  private readonly now: () => number;
  private readonly random: () => number;
  private readonly parser: MavlinkParser;
  private readonly streams: StreamSpec[];
  private readonly parameters: ParamRecord[];
  private readonly home: { lat: number; lng: number; altM: number };

  private sequence = 0;
  private armed = false;
  private mode: number = COPTER_MODES.STABILIZE;
  private batteryPercent = 100;
  private latitude: number;
  private longitude: number;
  private altitudeM: number;
  private roll = 0;
  private pitch = 0;
  private yaw = 0;
  private satellites = 14;
  private bootMs = 0;
  private lastTickAt: number;
  private fault: SimulatedFault = 'none';
  private paramCursor: number | null = null;
  private readonly mission = new SimMission();
  /** 最近一次发起航线握手的对端（地面站），回应要发给它而不是广播 */
  private missionPeer: { system: number; component: number } = { system: 255, component: 0 };
  /** 磁力计校准的进度播报剩余步数，null 表示没有校准在进行 */
  private magCalSteps: number | null = null;
  private vibX = VIBRATION_BASE_DISARMED;
  private vibY = VIBRATION_BASE_DISARMED;
  private vibZ = VIBRATION_BASE_DISARMED * 1.5;
  /** 三个加速度计的累计裁剪次数：只增不减，与上游 `get_accel_clip_count()` 语义一致 */
  private readonly accelClipCounts: [number, number, number] = [0, 0, 0];
  /** 正在飞向的航点序号（就是 `MISSION_CURRENT.seq`） */
  private missionCurrentSeq = 0;
  /** 在本次航点上已经停了多久，用来推进 `missionCurrentSeq` */
  private missionDwellMs = 0;

  constructor(options: SimulatedVehicleOptions = {}) {
    this.systemId = options.systemId ?? 1;
    this.componentId = options.componentId ?? 1;
    this.kind = options.kind ?? 'copter';
    this.version = options.version ?? 2;
    this.now = options.now ?? (() => Date.now());
    this.random = options.random ?? Math.random;

    this.home = {
      lat: options.homeLat ?? 31.2304,
      lng: options.homeLng ?? 121.4737,
      altM: options.homeAltitudeM ?? 12,
    };
    this.latitude = this.home.lat;
    this.longitude = this.home.lng;
    this.altitudeM = this.home.altM;
    this.lastTickAt = this.now();

    this.parameters = PARAMETERS.map(([name, value]) => ({ name, value }));
    this.streams = DEFAULT_STREAMS.map(([name, rateHz]) => ({ name, rateHz, lastSentAt: 0 }));
    this.parser = new MavlinkParser({
      now: this.now,
      onFrame: (frame) => this.handleFrame(frame),
    });
  }

  connect(sink: (bytes: Uint8Array) => void): void {
    this.sink = sink;
  }

  disconnect(): void {
    this.sink = null;
  }

  receive(bytes: Uint8Array): void {
    // `silent-commands` 模拟的是**上行链路已断**：机体根本收不到指令，
    // 所以这里直接丢弃，而不是"处理后不回话"。
    // 这个区别不是细节：如果指令照旧生效、只是 ACK 丢了，
    // 那台飞机是真的会执行一条地面站以为失败了的命令。
    if (this.fault === 'silent-commands') return;
    this.parser.push(bytes);
  }

  setFault(fault: SimulatedFault): void {
    this.fault = fault;
  }

  get currentFault(): SimulatedFault {
    return this.fault;
  }

  snapshot(): SimulatedVehicleSnapshot {
    return {
      armed: this.armed,
      mode: this.mode,
      modeName: this.modeName(this.mode),
      batteryPercent: this.batteryPercent,
      latitude: this.latitude,
      longitude: this.longitude,
      altitudeM: this.altitudeM,
      satellites: this.satellites,
    };
  }

  /** 手动注入一条状态文本（状态文本是绝大多数告警的来源） */
  announce(text: string, severity = 6): void {
    this.emit('STATUSTEXT', { severity, text });
  }

  /** 设置可见卫星数，用于制造 GPS 相关的 PreArm 拒绝 */
  setSatellites(count: number): void {
    this.satellites = count;
  }

  /** 推进时间：更新模型并按各自频率发帧。时间由外部驱动，便于测试精确控制。 */
  tick(at: number = this.now()): void {
    const elapsed = Math.max(0, at - this.lastTickAt);
    this.lastTickAt = at;
    if (this.fault !== 'frozen') {
      this.bootMs += elapsed;
      this.advance(elapsed);
    }
    if (this.fault === 'offline') return;

    for (const stream of this.streams) {
      if (stream.rateHz <= 0) continue;
      if (at - stream.lastSentAt < 1000 / stream.rateHz) continue;
      stream.lastSentAt = at;
      this.emitStream(stream.name);
    }

    this.pumpParameterStream();
    this.pumpMagCal();
  }

  private advance(elapsedMs: number): void {
    const seconds = elapsedMs / 1000;

    // 上锁时缓慢自放；解锁后按约 20A 放电（5200mAh 约 15 分钟）
    const drainPerSecond = this.armed ? 100 / (15 * 60) : 100 / (24 * 3600);
    this.batteryPercent = Math.max(0, this.batteryPercent - drainPerSecond * seconds);

    const targetRoll = this.armed ? 0.06 : 0;
    const targetPitch = this.armed ? -0.03 : 0;
    this.roll += (targetRoll - this.roll) * Math.min(1, seconds * 2);
    this.pitch += (targetPitch - this.pitch) * Math.min(1, seconds * 2);

    const airborne =
      this.armed &&
      (this.mode === COPTER_MODES.GUIDED ||
        this.mode === COPTER_MODES.AUTO ||
        this.mode === COPTER_MODES.LOITER ||
        this.mode === COPTER_MODES.RTL ||
        this.mode === COPTER_MODES.ALT_HOLD);

    if (airborne) {
      this.yaw = (this.yaw + seconds * 0.15) % (Math.PI * 2);
      this.altitudeM += (this.home.altM + 30 - this.altitudeM) * Math.min(1, seconds * 0.3);
      const radiusM = 40;
      const angle = (this.bootMs / 1000) * 0.15;
      this.latitude = this.home.lat + (radiusM / 111_320) * Math.sin(angle);
      this.longitude =
        this.home.lng +
        (radiusM / (111_320 * Math.cos((this.home.lat * Math.PI) / 180))) * Math.cos(angle);
    } else if (!this.armed) {
      this.altitudeM = this.home.altM;
      this.latitude = this.home.lat;
      this.longitude = this.home.lng;
      this.yaw = 0;
    }

    this.updateVibration();
    this.updateMissionProgress(elapsedMs);
  }

  /**
   * 振动模型。
   *
   * 用若干条不同周期的正弦叠加，而不是随机游走：随机看起来像噪声，但相邻两帧的
   * 相关性会被抹掉，曲线变成一团毛刺，"趋势"这个东西就看不出来了 ——
   * 而振动面板真正要回答的正是"最近是不是在变差"。相位取 `bootMs`，
   * 所以时间推进时形状是连续的。
   *
   * 抖动**刻意不用** `this.random()`：注入的伪随机源要留给 `flaky` 故障，
   * 多消耗一次抽样就会改变"第几帧被丢"，让已有的丢帧复现测试不再可复现。
   * 这里用几个互质周期的高频正弦当噪声源：够像噪声，又完全确定。
   */
  private updateVibration(): void {
    const t = this.bootMs / 1000;
    const noise = (offset: number): number =>
      Math.sin(t * 7.3 + offset) * 0.6 + Math.sin(t * 13.7 + offset * 2) * 0.4;

    const base = this.armed ? VIBRATION_BASE_ARMED : VIBRATION_BASE_DISARMED;
    // 垂直轴比水平轴大：下洗流与机架共振最主要的传播方向就是 Z
    const x = base + Math.sin(t * 0.7) * 3 + noise(0);
    const y = base * 0.85 + Math.sin(t * 0.53 + 1.1) * 2.6 + noise(1.7);
    const z = base * 1.5 + Math.sin(t * 0.31) * 8 + noise(3.1);

    this.vibX = Math.max(0, x);
    this.vibY = Math.max(0, y);
    this.vibZ = Math.max(0, z);

    // 电机不转时不该出现裁剪：真机上未解锁的机体连 IMU 的机械激励都没有
    if (!this.armed) return;
    if (x > VIBRATION_CLIP_THRESHOLD) this.accelClipCounts[0] += 1;
    if (y > VIBRATION_CLIP_THRESHOLD) this.accelClipCounts[1] += 1;
    if (z > VIBRATION_CLIP_THRESHOLD) this.accelClipCounts[2] += 1;
  }

  /**
   * 任务执行推进：只有真的在跑航线时才动 `missionCurrentSeq`。
   *
   * 进度条读的就是这个数，所以它**必须**在任务执行时真的往前走 ——
   * 否则界面永远停在"第 0 个航点"，而这类"界面不动"最难判断是界面的问题
   * 还是机体的问题。
   *
   * 到最后一个航点就停下，不循环：真机跑完任务会停在最后一点（或转 RTL），
   * 循环反而让"任务已结束"这个状态在模拟机里根本不存在。
   */
  private updateMissionProgress(elapsedMs: number): void {
    const total = this.mission.countOf(0);
    if (total === 0) {
      this.missionCurrentSeq = 0;
      this.missionDwellMs = 0;
      return;
    }
    // 航线被换掉（重新上传 / 清空）之后旧游标可能已经越界
    if (this.missionCurrentSeq >= total) this.missionCurrentSeq = 0;

    if (!this.armed || this.mode !== COPTER_MODES.AUTO || this.missionCurrentSeq >= total - 1) {
      this.missionDwellMs = 0;
      return;
    }

    this.missionDwellMs += elapsedMs;
    while (this.missionDwellMs >= MISSION_ITEM_DWELL_MS && this.missionCurrentSeq < total - 1) {
      this.missionDwellMs -= MISSION_ITEM_DWELL_MS;
      /*
       * 两条消息的含义不同，顺序也不能反：`MISSION_ITEM_REACHED` 说的是
       * "刚到的是哪一个"（状态条要打勾的那个），`MISSION_CURRENT` 说的是
       * "正在飞向的是哪一个"。先报到达再挪目标。
       */
      this.emit('MISSION_ITEM_REACHED', { seq: this.missionCurrentSeq });
      this.missionCurrentSeq += 1;
    }
  }

  private emitStream(name: string): void {
    const boot = Math.floor(this.bootMs);
    const heading = Math.round(((this.yaw * 180) / Math.PI + 360) % 360);

    switch (name) {
      case 'HEARTBEAT':
        this.emit('HEARTBEAT', {
          type: this.kind === 'plane' ? MAV_TYPE_FIXED_WING : MAV_TYPE_QUADROTOR,
          autopilot: AUTOPILOT_ARDUPILOTMEGA,
          base_mode: this.baseMode(),
          custom_mode: this.mode,
          system_status: this.armed ? MAV_STATE_ACTIVE : MAV_STATE_STANDBY,
        });
        return;
      case 'SYS_STATUS':
        this.emit('SYS_STATUS', {
          onboard_control_sensors_present: 0x2_0ff_ffff,
          onboard_control_sensors_enabled: 0x2_0ff_efff,
          onboard_control_sensors_health: 0x2_0ff_efff,
          load: 320,
          voltage_battery: this.batteryVoltageMv(),
          current_battery: this.armed ? 1850 : 40,
          // `battery_remaining` 是 int8 的**百分比**（0~100），不是百分之一。
          // 乘 100 会溢出成 10000 & 0xFF = 16，于是满电的飞机报 16% ——
          // 这种错法特别隐蔽：数值看起来是个合理的百分比。
          battery_remaining: Math.round(this.batteryPercent),
          drop_rate_comm: 0,
          errors_comm: 0,
          errors_count1: 0,
          errors_count2: 0,
          errors_count3: 0,
          errors_count4: 0,
        });
        return;
      case 'ATTITUDE':
        this.emit('ATTITUDE', {
          time_boot_ms: boot,
          roll: this.roll,
          pitch: this.pitch,
          yaw: this.yaw,
          rollspeed: 0.01,
          pitchspeed: -0.005,
          yawspeed: 0.02,
        });
        return;
      case 'GLOBAL_POSITION_INT':
        this.emit('GLOBAL_POSITION_INT', {
          time_boot_ms: boot,
          lat: Math.round(this.latitude * 1e7),
          lon: Math.round(this.longitude * 1e7),
          alt: Math.round(this.altitudeM * 1000),
          relative_alt: Math.round((this.altitudeM - this.home.altM) * 1000),
          vx: this.armed ? 120 : 0,
          vy: this.armed ? -80 : 0,
          vz: 0,
          hdg: this.yaw === 0 && !this.armed ? 65535 : heading * 100,
        });
        return;
      case 'GPS_RAW_INT':
        this.emit('GPS_RAW_INT', {
          time_usec: BigInt(boot) * 1000n,
          fix_type: this.satellites >= 10 ? 3 : 1,
          lat: Math.round(this.latitude * 1e7),
          lon: Math.round(this.longitude * 1e7),
          alt: Math.round((this.altitudeM + 5) * 1000),
          eph: 90,
          epv: 130,
          vel: this.armed ? 150 : 0,
          cog: heading * 100,
          satellites_visible: this.satellites,
        });
        return;
      case 'VFR_HUD':
        this.emit('VFR_HUD', {
          airspeed: this.armed ? 12.5 : 0,
          groundspeed: this.armed ? 14 : 0,
          alt: this.altitudeM,
          climb: this.armed ? 0.2 : 0,
          heading,
          throttle: this.armed ? 45 : 0,
        });
        return;
      case 'BATTERY_STATUS':
        this.emit('BATTERY_STATUS', {
          id: 0,
          battery_function: 2,
          type: 2,
          temperature: 5231,
          voltages: [
            this.batteryVoltageMv(),
            65535,
            65535,
            65535,
            65535,
            65535,
            65535,
            65535,
            65535,
            65535,
          ],
          current_battery: this.armed ? 1850 : 40,
          current_consumed: Math.round((100 - this.batteryPercent) * 52),
          energy_consumed: Math.round((100 - this.batteryPercent) * 7.3),
          battery_remaining: Math.round(this.batteryPercent),
        });
        return;
      case 'RC_CHANNELS':
        this.emit('RC_CHANNELS', {
          time_boot_ms: boot,
          chancount: 16,
          chan1_raw: 1500,
          chan2_raw: 1500,
          chan3_raw: this.armed ? 1450 : 1000,
          chan4_raw: 1500,
          chan5_raw: 1100,
          chan6_raw: 1100,
          chan7_raw: 1100,
          chan8_raw: 1100,
          chan9_raw: 0,
          chan10_raw: 0,
          chan11_raw: 0,
          chan12_raw: 0,
          chan13_raw: 0,
          chan14_raw: 0,
          chan15_raw: 0,
          chan16_raw: 0,
          chan17_raw: 0,
          chan18_raw: 0,
          rssi: 200,
        });
        return;
      case 'EKF_STATUS_REPORT':
        this.emit('EKF_STATUS_REPORT', {
          // 姿态 / 水平位置 / 垂直位置 / 罗盘 / 地形 全部健康
          flags: 0x1f,
          velocity_variance: 0.02,
          pos_horiz_variance: 0.05,
          pos_vert_variance: 0.08,
          compass_variance: 0.01,
          terrain_alt_variance: 0,
        });
        return;
      case 'VIBRATION':
        this.emit('VIBRATION', {
          time_usec: BigInt(boot) * 1000n,
          vibration_x: this.vibX,
          vibration_y: this.vibY,
          vibration_z: this.vibZ,
          // 三个加速度计各自的累计裁剪次数。上游是分开报的，
          // 合成一个数会让"哪个轴在打到量程"这个排查方向直接消失
          clipping_0: this.accelClipCounts[0],
          clipping_1: this.accelClipCounts[1],
          clipping_2: this.accelClipCounts[2],
        });
        return;
      case 'MISSION_CURRENT':
        this.emit('MISSION_CURRENT', {
          seq: this.missionCurrentSeq,
          /*
           * 没装航线时就是 0。核心层会把 0 当成"没有总数"（`total` 是 MAVLink 2 的
           * 扩展字段，会被尾部零字节截断，读到的 0 分不清"真的没有航点"和
           * "对端根本没带这个字段"），所以这里给 0 是安全的 ——
           * 它不会被界面显示成"共 0 个航点"。
           */
          total: this.mission.countOf(0),
        });
        return;
      default:
        return;
    }
  }

  private baseMode(): number {
    let mode = MAV_MODE_FLAG_CUSTOM_MODE_ENABLED | MAV_MODE_FLAG_STABILIZE_ENABLED;
    if (this.mode === COPTER_MODES.AUTO) mode |= MAV_MODE_FLAG_AUTO_ENABLED;
    if (this.mode === COPTER_MODES.GUIDED) mode |= MAV_MODE_FLAG_GUIDED_ENABLED;
    if (this.armed) mode |= MAV_MODE_FLAG_SAFETY_ARMED;
    return mode;
  }

  private batteryVoltageMv(): number {
    // 4S：满电 16.8V，空电 13.2V
    return Math.round(13_200 + (this.batteryPercent / 100) * 3600);
  }

  private emit(name: string, values: MessageValues): void {
    if (this.sink === null || this.fault === 'offline') return;
    if (this.fault === 'flaky' && this.random() < 0.15) return;

    const definition = messageByName(name);
    if (definition === undefined) return;

    const frame = encodeFrame(definition, values, {
      version: this.version,
      sequence: this.sequence,
      systemId: this.systemId,
      componentId: this.componentId,
    });
    this.sequence = (this.sequence + 1) % 256;
    this.sink(frame);
  }

  // -------------------------------------------------------------------------
  // 协议面：把地面站发来的帧翻译成状态变化
  // -------------------------------------------------------------------------

  private handleFrame(frame: MavlinkFrame): void {
    const definition = messageByNameById(frame.messageId);
    if (definition === undefined) return;
    const values = decodePayload(definition, frame.payload);
    const numberField = (name: string): number | null => {
      const value = values[name];
      return typeof value === 'number' ? value : null;
    };
    const stringField = (name: string): string | null => {
      const value = values[name];
      return typeof value === 'string' ? value : null;
    };

    switch (definition.name) {
      case 'COMMAND_LONG':
        this.handleCommandLong(frame, numberField);
        return;
      case 'PARAM_REQUEST_LIST':
        this.paramCursor = 0;
        return;
      case 'PARAM_REQUEST_READ': {
        const index = numberField('param_index');
        if (index !== null && index >= 0 && index < this.parameters.length) {
          this.emitParamValue(index);
          return;
        }
        const name = stringField('param_id');
        if (name !== null) {
          const position = this.parameters.findIndex((record) => record.name === name);
          if (position >= 0) this.emitParamValue(position);
        }
        return;
      }
      case 'PARAM_SET': {
        const name = stringField('param_id');
        const value = numberField('param_value');
        if (name === null || value === null) return;
        const position = this.parameters.findIndex((record) => record.name === name);
        const record = position < 0 ? undefined : this.parameters[position];
        if (record === undefined) return;
        record.value = value;
        this.emitParamValue(position);
        return;
      }
      // ---------------------------------------------------------------------
      // 航线传输：地面站 → 机体
      // ---------------------------------------------------------------------
      case 'MISSION_COUNT': {
        this.rememberPeer(frame);
        const missionType = numberField('mission_type') ?? 0;
        const step = this.mission.beginUpload(numberField('count') ?? 0, missionType);
        this.answerUploadStep(step, missionType, numberField('opaque_id') ?? 0);
        return;
      }
      case 'MISSION_ITEM_INT':
      case 'MISSION_ITEM': {
        const item = missionItemFrom(values, definition.name === 'MISSION_ITEM');
        if (item === null) return;
        const missionType = numberField('mission_type') ?? 0;
        const step = this.mission.stageItem(item);
        this.answerUploadStep(step, missionType, 0);
        return;
      }
      // ---------------------------------------------------------------------
      // 航线传输：机体 → 地面站
      // ---------------------------------------------------------------------
      case 'MISSION_REQUEST_LIST': {
        this.rememberPeer(frame);
        const missionType = numberField('mission_type') ?? 0;
        const total = this.mission.beginDownload(missionType);
        this.emit('MISSION_COUNT', {
          count: total,
          target_system: frame.systemId,
          target_component: frame.componentId,
          mission_type: missionType,
        });
        return;
      }
      case 'MISSION_REQUEST_INT':
      case 'MISSION_REQUEST': {
        // 无论对端要 INT 还是 float 版本，都回 INT：这是 ArduPilot 的行为，
        // 也是唯一不会在纬度上丢精度的选择
        const item = this.mission.nextDownloadItem();
        const missionType = numberField('mission_type') ?? 0;
        if (item === null) {
          this.emitMissionAck(MISSION_INVALID, missionType, 0);
          return;
        }
        this.emit('MISSION_ITEM_INT', {
          param1: item.param1,
          param2: item.param2,
          param3: item.param3,
          param4: item.param4,
          x: item.x,
          y: item.y,
          z: item.z,
          seq: item.seq,
          command: item.command,
          target_system: this.missionPeer.system,
          target_component: this.missionPeer.component,
          frame: item.frame,
          current: item.current,
          autocontinue: item.autocontinue,
          mission_type: missionType,
        });
        return;
      }
      case 'MISSION_CLEAR_ALL': {
        this.rememberPeer(frame);
        const missionType = numberField('mission_type') ?? 0;
        if (missionType === 255) this.mission.clearAll();
        else this.mission.clear(missionType);
        this.emitMissionAck(MISSION_ACCEPTED, missionType, 0);
        return;
      }
      case 'MISSION_ACK':
        // 地面站确认已收妥最后一条，下载到此结束
        this.mission.endDownload();
        return;
      default:
        return;
    }
  }

  private rememberPeer(frame: MavlinkFrame): void {
    this.missionPeer = { system: frame.systemId, component: frame.componentId };
  }

  private answerUploadStep(step: MissionUploadStep, missionType: number, opaqueId: number): void {
    switch (step.action) {
      case 'request':
        this.emit('MISSION_REQUEST_INT', {
          seq: step.seq,
          target_system: this.missionPeer.system,
          target_component: this.missionPeer.component,
          mission_type: missionType,
        });
        return;
      case 'stored':
        this.emitMissionAck(MISSION_ACCEPTED, missionType, opaqueId);
        return;
      case 'invalid':
        this.emitMissionAck(
          this.mission.uploading ? MISSION_UNSUPPORTED : MISSION_ERROR,
          missionType,
          opaqueId,
        );
        return;
    }
  }

  private emitMissionAck(type: number, missionType: number, opaqueId: number): void {
    this.emit('MISSION_ACK', {
      target_system: this.missionPeer.system,
      target_component: this.missionPeer.component,
      type,
      mission_type: missionType,
      // 回显 `opaque_id` 是 MAVLink 2 的约定：上传方靠它把回应和自己那次上传对上
      opaque_id: opaqueId,
    });
  }

  /** 对外暴露的只读航线视图，供界面上"机体上现在是什么"与本地计划做比对 */
  get missionStore(): SimMission {
    return this.mission;
  }

  private handleCommandLong(
    frame: MavlinkFrame,
    field: (name: string) => number | null,
  ): void {
    const command = field('command') ?? 0;
    const param = (index: number): number => field(`param${index}`) ?? 0;
    let result: number = MAV_RESULT_ACCEPTED;

    switch (command) {
      case SIM_COMMANDS.COMPONENT_ARM_DISARM: {
        const wantArmed = param(1) >= 0.5;
        if (wantArmed && this.satellites < 6) {
          // 真实 ArduPilot 会做 PreArm 检查并拒绝，理由经 STATUSTEXT 告诉操作员
          this.announce('PreArm: GPS 卫星数不足', 4);
          result = MAV_RESULT_FAILED;
        } else {
          this.armed = wantArmed;
          this.announce(wantArmed ? 'Armed' : 'Disarmed');
        }
        break;
      }
      case SIM_COMMANDS.DO_SET_MODE: {
        const customMode = param(2);
        if (this.isKnownMode(customMode)) {
          this.mode = customMode;
          this.announce(`Flight mode: ${this.modeName(customMode)}`);
        } else {
          result = MAV_RESULT_UNSUPPORTED;
        }
        break;
      }
      case SIM_COMMANDS.NAV_TAKEOFF:
        if (!this.armed) {
          this.announce('PreArm: 起飞前需要先解锁电机', 4);
          result = MAV_RESULT_FAILED;
        } else {
          this.mode = COPTER_MODES.GUIDED;
        }
        break;
      case SIM_COMMANDS.NAV_LAND:
        this.mode = COPTER_MODES.LAND;
        break;
      case SIM_COMMANDS.NAV_RETURN_TO_LAUNCH:
        this.mode = COPTER_MODES.RTL;
        break;
      case SIM_COMMANDS.RUN_PREARM_CHECKS:
        this.announce('PreArm: 检查通过');
        break;
      case SIM_COMMANDS.SET_MESSAGE_INTERVAL:
        this.applyMessageInterval(param(1), param(2));
        break;
      case SIM_COMMANDS.REQUEST_MESSAGE: {
        const requested = Math.round(param(1));
        if (requested === SIM_REQUESTABLE_MESSAGES.AUTOPILOT_VERSION) {
          this.emitAutopilotVersion();
        } else if (requested === SIM_REQUESTABLE_MESSAGES.AVAILABLE_MODES) {
          this.emitAvailableModes();
        } else if (requested === SIM_REQUESTABLE_MESSAGES.HOME_POSITION) {
          this.emitHomePosition();
        } else {
          result = MAV_RESULT_UNSUPPORTED;
        }
        break;
      }
      case SIM_COMMANDS.PREFLIGHT_CALIBRATION:
        // param5 是罗盘。ArduPilot 允许一次只校一项，这里只实现罗盘，
        // 其余项明确回"不支持"而不是假装接受
        if (param(5) >= 0.5) this.startMagCal();
        else result = MAV_RESULT_UNSUPPORTED;
        break;
      case SIM_COMMANDS.DO_START_MAG_CAL:
        this.startMagCal();
        break;
      case SIM_COMMANDS.DO_SET_HOME: {
        if (param(1) >= 0.5) {
          this.home.lat = this.latitude;
          this.home.lng = this.longitude;
          this.home.altM = this.altitudeM;
        } else {
          this.home.lat = param(5);
          this.home.lng = param(6);
          this.home.altM = param(7);
        }
        this.announce('已设置返航点');
        break;
      }
      case SIM_COMMANDS.MISSION_START:
        if (!this.armed) {
          // 未解锁就起飞是实际排障里出现最多的一类"任务是好的但飞机不动"
          result = MAV_RESULT_FAILED;
          this.announce('PreArm: 未解锁，无法开始任务');
        } else {
          // 真机上"开始任务"就是切到 AUTO 并从第一个航点跑起。
          // 只回 ACK 而不切模式的话，进度条永远不会有东西可显示
          this.mode = COPTER_MODES.AUTO;
          this.missionCurrentSeq = 0;
          this.missionDwellMs = 0;
        }
        break;
      default:
        result = MAV_RESULT_UNSUPPORTED;
        break;
    }

    this.emit('COMMAND_ACK', {
      command,
      result,
      target_system: frame.systemId,
      target_component: frame.componentId,
    });
  }

  private applyMessageInterval(messageId: number, intervalUs: number): void {
    const definition = messageByNameById(messageId);
    if (definition === undefined) return;
    const stream = this.streams.find((candidate) => candidate.name === definition.name);
    if (stream === undefined) return;
    // intervalUs = -1 表示停止推送
    stream.rateHz = intervalUs < 0 ? 0 : 1_000_000 / Math.max(1, intervalUs);
  }

  /**
   * 参数列表逐条回送。
   *
   * 一次 tick 回多条：真实链路把参数当流来推（57600 波特下每秒几十条很正常），
   * 一条一个 tick 会让 1600 个参数的机体下载耗时以分钟计。
   */
  private pumpParameterStream(): void {
    if (this.paramCursor === null) return;
    for (let sent = 0; sent < PARAMS_PER_TICK; sent += 1) {
      if (this.paramCursor >= this.parameters.length) {
        this.paramCursor = null;
        return;
      }
      this.emitParamValue(this.paramCursor);
      this.paramCursor += 1;
    }
  }

  private emitParamValue(index: number): void {
    const record = this.parameters[index];
    if (record === undefined) return;
    this.emit('PARAM_VALUE', {
      param_id: record.name,
      param_value: record.value,
      param_type: PARAM_TYPE_REAL32,
      param_count: this.parameters.length,
      param_index: index,
    });
  }

  // -------------------------------------------------------------------------
  // 能力与版本：`MAV_CMD_REQUEST_MESSAGE` 的回话
  // -------------------------------------------------------------------------

  private emitAutopilotVersion(): void {
    const major = 4;
    const minor = 7;
    const patch = 1;
    this.emit('AUTOPILOT_VERSION', {
      capabilities: SIM_PROTOCOL_CAPABILITIES,
      // uid 是 uint64，超过 2^53 之后 JS 的 number 已经无法精确表示，
      // 所以这里必须用 bigint —— 它正是"要用 bigint"的典型判据
      uid: 0x0011223344556677n,
      flight_sw_version: (major << 24) | (minor << 16) | (patch << 8),
      middleware_sw_version: 0,
      os_sw_version: 0,
      board_version: 0,
      vendor_id: 0x1209,
      product_id: 0x5741,
      flight_custom_version: [0, 0, 0, 0, 0, 0, 0, 0],
      middleware_custom_version: [0, 0, 0, 0, 0, 0, 0, 0],
      os_custom_version: [0, 0, 0, 0, 0, 0, 0, 0],
    });
  }

  /**
   * 逐个播报可用飞行模式。
   *
   * `AVAILABLE_MODES` 一次只承载**一个**模式（靠 `mode_index` 区分），
   * 所以"可用模式列表"在协议上是一串消息而不是一条 —— 上层必须按流来收，
   * 不能"收到一条就当成全部"。这里连发就是为了让上层没法蒙对。
   */
  private emitAvailableModes(): void {
    const modes = Object.values(COPTER_MODES)
      .map((value) => ({ value, name: this.modeName(value) }))
      .sort((a, b) => a.value - b.value);

    for (const [index, mode] of modes.entries()) {
      this.emit('AVAILABLE_MODES', {
        custom_mode: mode.value,
        properties: 0,
        number_modes: modes.length,
        mode_index: index,
        // MAV_STANDARD_MODE_NON_STANDARD：ArduPilot 的模式不属于标准模式集合
        standard_mode: 0,
        mode_name: mode.name,
        seq: index,
      });
    }
  }

  private emitHomePosition(): void {
    this.emit('HOME_POSITION', {
      latitude: Math.round(this.home.lat * 1e7),
      longitude: Math.round(this.home.lng * 1e7),
      altitude: Math.round(this.home.altM * 1000),
      x: 0,
      y: 0,
      z: 0,
      q: [1, 0, 0, 0],
      approach_x: 0,
      approach_y: 0,
      approach_z: 0,
    });
  }

  // -------------------------------------------------------------------------
  // 磁力计校准：一条有过程、有结论的命令
  // -------------------------------------------------------------------------

  private startMagCal(): void {
    // 校准是"有进度、最后有结论"的命令里最典型的一种。
    // 中间态必须真的发出来，否则界面上的进度条永远测不到东西。
    this.magCalSteps = MAG_CAL_PROGRESS_STEPS;
    this.announce('磁力计校准已开始，请旋转机体');
  }

  private pumpMagCal(): void {
    const remaining = this.magCalSteps;
    if (remaining === null) return;
    if (remaining > 0) {
      const completed = MAG_CAL_PROGRESS_STEPS - remaining;
      const percent = Math.round((completed / MAG_CAL_PROGRESS_STEPS) * 100);
      const mask = new Array<number>(10).fill(0);
      mask[0] = 1;
      this.emit('MAG_CAL_PROGRESS', {
        direction_x: 0,
        direction_y: 0,
        direction_z: 0,
        compass_id: 0,
        cal_mask: 1,
        cal_status: MAG_CAL_RUNNING_STEP_ONE,
        attempt: 0,
        completion_pct: percent,
        completion_mask: mask,
      });
      this.magCalSteps = remaining - 1;
      return;
    }

    this.magCalSteps = null;
    this.emit('MAG_CAL_REPORT', {
      // 真实机体的 fitness 通常在 5~15 之间，>30 一般是干扰太大
      fitness: 8.4,
      ofs_x: 12.5,
      ofs_y: -30.2,
      ofs_z: 4.8,
      diag_x: 1.002,
      diag_y: 0.998,
      diag_z: 1.001,
      offdiag_x: 0.001,
      offdiag_y: -0.002,
      offdiag_z: 0.0005,
      compass_id: 0,
      cal_mask: 1,
      cal_status: MAG_CAL_SUCCESS,
      autosaved: 1,
      orientation_confidence: 1,
      old_orientation: 0,
      new_orientation: 0,
      scale_factor: 1,
    });
    this.announce('磁力计校准完成');
  }

  private isKnownMode(mode: number): boolean {
    return Object.values(COPTER_MODES).some((candidate) => candidate === mode);
  }

  private modeName(mode: number): string {
    const found = Object.entries(COPTER_MODES).find(([, value]) => value === mode);
    return found === undefined ? `MODE_${mode}` : found[0];
  }
}

let byIdIndex: Map<number, NonNullable<ReturnType<typeof messageByName>>> | null = null;

/**
 * 按消息 ID 反查定义。
 *
 * 反向索引懒建：绝大多数运行时间只需要按名字查（`messageByName` 内部已有索引），
 * 而反查只在**收到**帧时才需要 —— 启动时就把 326 条消息建成两张表没有必要。
 */
function messageByNameById(id: number): NonNullable<ReturnType<typeof messageByName>> | undefined {
  if (byIdIndex === null) {
    byIdIndex = new Map();
    for (const definition of allMessages()) byIdIndex.set(definition.id, definition);
  }
  return byIdIndex.get(id);
}

/**
 * 把收到的航线项统一成整数表示。
 *
 * `MISSION_ITEM`（float 版）的 x/y 是**十进制度**，`MISSION_ITEM_INT` 是 ×1e7 的整数。
 * 两者混进来时必须先归一，否则一条 float 航线会被当成"经度 0.00 度"存进去 ——
 * 那是几内亚湾，不是用户选的地方。
 */
function missionItemFrom(values: MessageValues, fromFloat: boolean): SimMissionItem | null {
  const number = (name: string): number => {
    const value = values[name];
    return typeof value === 'number' ? value : 0;
  };
  const x = number('x');
  const y = number('y');
  const seq = number('seq');
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;

  return {
    seq,
    frame: number('frame'),
    command: number('command'),
    current: number('current'),
    autocontinue: number('autocontinue'),
    param1: number('param1'),
    param2: number('param2'),
    param3: number('param3'),
    param4: number('param4'),
    x: fromFloat ? Math.round(x * 1e7) : x,
    y: fromFloat ? Math.round(y * 1e7) : y,
    z: number('z'),
  };
}
